import { Injectable, inject } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { Observable, throwError, combineLatest, of, map, catchError, switchMap, first, tap } from 'rxjs';

import { ConfigService } from '@fcom/core';
import { loginToken, profile } from '@fcom/core/selectors/login.selector';
import { Profile } from '@fcom/core-api/login/interfaces/login.interface';
import { FinnairOrderPreview } from '@fcom/dapi/api/models/finnair-order-preview';
import { FinnairOrderPreviewsResponse } from '@fcom/dapi/api/models/finnair-order-previews-response';
import { finShare } from '@fcom/rx';
import { OrderPreviewsService } from '@fcom/manage-booking-common';

import { Trip, AddFrequentFlyerResponse, Journey } from '../interfaces/pax-trip.interface';
import * as TripsActions from '../store/actions/trips.actions';
import { FetchTripsStatus, State } from '../interfaces/store.interface';

@Injectable()
export class PaxTripService {
  private http = inject(HttpClient);
  private orderPreviewsService = inject(OrderPreviewsService);
  private store$ = inject<Store<State>>(Store);
  private configService = inject(ConfigService);

  private accessToken$: Observable<string>;
  private profile$: Observable<Profile>;

  constructor() {
    this.accessToken$ = this.store$.pipe(loginToken(), first(Boolean)) as Observable<string>;
    this.profile$ = this.store$.pipe(profile(), first(Boolean)) as Observable<Profile>;
  }

  getTrips(): Observable<Trip[]> {
    return this.orderPreviewsService.getUpcomingTrips().pipe(
      map((response: FinnairOrderPreviewsResponse) =>
        this.mapFetchOrderPreviewsResponse(response.orderPreviews, response.locations)
      ),
      finShare()
    );
  }

  addTrip(bookingReference: string): Observable<AddFrequentFlyerResponse> {
    return combineLatest([this.accessToken$, this.profile$]).pipe(
      switchMap(([token, memberProfile]: [string, Profile]) => {
        const apiUrl = `${this.configService.cfg.bookingServiceUrl}/booking/${bookingReference}/${memberProfile.lastname}/addFrequentFlyer`;
        const keyId = this.configService.cfg.bookingServiceApiKey;

        const httpOptions = {
          headers: new HttpHeaders({
            oauth_token: token,
            'X-Api-Key': keyId,
            'Content-Type': 'application/json',
          }),
        };

        const params = {
          firstName: memberProfile.firstname,
          frequentFlyerNumber: memberProfile.memberNumber,
        };
        return this.http.post<AddFrequentFlyerResponse>(apiUrl, params, httpOptions);
      }),
      map((response) => {
        if (response.status.message === 'OK') {
          return response;
        }
        throw new Error('Error when adding booking');
      }),
      catchError((err: unknown) => {
        return throwError(() => err);
      })
    );
  }

  verifyAddedTrip(bookingReference: string): Observable<boolean> {
    return this.getTrips().pipe(
      tap((trips: Trip[]) => {
        this.store$.dispatch(TripsActions.setTrips({ trips: trips, status: FetchTripsStatus.OK }));
      }),
      map((trips: Trip[]) => trips.some((trip) => trip.recLoc === bookingReference)),
      catchError(() => {
        this.store$.dispatch(TripsActions.setTrips({ trips: [], status: FetchTripsStatus.ERROR }));
        return of(false);
      })
    );
  }

  private mapFetchOrderPreviewsResponse(
    orderPreviews: FinnairOrderPreview[],
    locations: FinnairOrderPreviewsResponse['locations']
  ): Trip[] {
    return (
      orderPreviews?.map((orderPreview) => {
        return {
          passengers: [
            {
              lastName: orderPreview.lastName,
            },
          ],
          journeys: this.mapBoundPreviewsToJourneys(orderPreview, locations),
          lastName: orderPreview.lastName,
          recLoc: orderPreview.id,
        };
      }) || []
    );
  }

  private mapBoundPreviewsToJourneys(
    orderPreview: FinnairOrderPreview,
    locations: FinnairOrderPreviewsResponse['locations']
  ): Journey[] {
    return (
      orderPreview.bounds?.map((boundPreview) => {
        const departureLocation = locations[boundPreview.departure.locationCode];
        const arrivalLocation = locations[boundPreview.arrival.locationCode];
        return {
          passengerAmount: orderPreview.numberOfTravelers,
          departure: {
            airport: boundPreview.departure.locationCode,
            airportName: departureLocation.name,
            municipality: departureLocation.cityName,
            scheduledDateTime: boundPreview.departure.dateTime,
          },
          arrival: {
            airport: boundPreview.arrival.locationCode,
            airportName: arrivalLocation.name,
            municipality: arrivalLocation.cityName,
            scheduledDateTime: boundPreview.arrival.dateTime,
          },
          recLoc: orderPreview.id,
        };
      }) || []
    );
  }
}
